import React, { useState, useEffect, useCallback, useRef } from "react";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import AssistantIcon from "@mui/icons-material/Assistant";
import { AMENITIES_MAP } from "../../utils/amenitiesMap";
import AdvancedFiltersModal from "./Filters";
import {
  Collapse,
  Box,
  TextField,
  Autocomplete,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  InputAdornment,
  Typography,
  Chip,
  useTheme,
} from "@mui/material";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import FilterListIcon from "@mui/icons-material/FilterList";
import TrainIcon from "@mui/icons-material/Train";
import DateRangeExplore from "./DateRangeExplore";
import Badge from "@mui/material/Badge";

// --- Helper to clean accents ---
const normalizeInput = (text) => {
  if (!text) return "";
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9\s-]/g, "");
};

export default function ExploreCompactSearchForm({
  origin,
  setOrigin,
  originInput,
  setOriginInput,
  originOptions,
  destination,
  setDestination,
  destinationInput,
  setDestinationInput,
  destinationOptions,
  departureDate, // Parent State
  setDepartureDate, // Parent Setter
  returnDate, // Parent State
  setReturnDate, // Parent Setter
  travelMode,
  onTravelModeChange,
  onSearch, // (AI Search)
  handleSearch, // (Standard Search)
  travelTime,
  setTravelTime,
  preferences,
  setPreferences,
  aiDescription,
  advancedFilters,
  setAdvancedFilters,
  airbnbType,
  setAirbnbType,
  filtersOpen,
  setFiltersOpen,
  setAiDescription,
  destinationVibe,
  setDestinationVibe,
  searchType,
  handleBackToSearch,
  setExploreResults,
  flightType,
  userCurrency,
}) {
  const [showPreferences, setShowPreferences] = useState(false);
  const theme = useTheme();

  // ✅ 1. LOCAL STATE BUFFER
  const [localDepartureDate, setLocalDepartureDate] = useState(departureDate);
  const [localReturnDate, setLocalReturnDate] = useState(returnDate);
  const [localTravelMode, setLocalTravelMode] = useState(travelMode);

  // ✅ 2. SYNC FROM PARENT
  useEffect(() => {
    setLocalDepartureDate(departureDate);
    setLocalReturnDate(returnDate);
  }, [departureDate, returnDate]);

  useEffect(() => {
    if (travelMode) setLocalTravelMode(travelMode);
  }, [travelMode]);

  const getSelectedFilterCount = () => {
    let count = 0;
    if (advancedFilters?.superhostOnly) count++;
    if (advancedFilters?.cancelPolicies?.length)
      count += advancedFilters.cancelPolicies.length;
    if (advancedFilters?.minPrice !== undefined) count++;
    if (
      advancedFilters?.maxPrice !== undefined &&
      advancedFilters.maxPrice < 300
    )
      count++;
    Object.entries(advancedFilters || {}).forEach(([key, value]) => {
      if (key.startsWith("amenity_") && value) count++;
    });
    if (advancedFilters?.flightStops?.length) count += advancedFilters.flightStops.length;
    return count;
  };

  // ✅ 3. FIXED SUBMIT HANDLER
  // ✅ 3. FIXED SUBMIT HANDLER (Wrapped in useCallback)
  const handleInternalSubmit = useCallback(() => {
    // A. Validate & Standardize Origin
    if (!origin) return;

    // SAFETY CHECK: Ensure origin is an Object.
    let finalOrigin = origin;
    if (typeof finalOrigin === "string") {
      finalOrigin = { city: finalOrigin, iata: "", country: "" };
    }

    // B. Validate Dates using LOCAL state
    if (!localDepartureDate || !localReturnDate) {
      // alert("Please select your travel dates to search."); // 👈 Comment out alert to prevent popup spam on currency switch
      return;
    }

    // C. COMMIT to Parent State (Home.js)
    setDepartureDate(localDepartureDate);
    setReturnDate(localReturnDate);

    if (onTravelModeChange) {
      onTravelModeChange(localTravelMode);
    }

    // ✅ D. EXPLORE MODE PRIORITY
    if (onSearch) {
      if (setExploreResults) {
        setExploreResults([]);
      }

      onSearch({
        origin: finalOrigin,
        departureDate: localDepartureDate,
        returnDate: localReturnDate,
        travelTime,
        transportMode: localTravelMode,
        preferences,
        description: aiDescription,
        destinationVibe,
        flightType: flightType || "roundtrip",
        tofly: localTravelMode === "flight",
        currency: userCurrency,
      });
      return;
    }

    // E. Fallback
    if (handleSearch) {
      handleSearch(searchType, advancedFilters, null, null, null, userCurrency);
    }
  }, [
    // 👈 Dependencies for useCallback
    origin,
    localDepartureDate,
    localReturnDate,
    onSearch,
    setExploreResults,
    travelTime,
    travelMode,
    preferences,
    aiDescription,
    destinationVibe,
    flightType,
    handleSearch,
    searchType,
    advancedFilters,
    setDepartureDate,
    setReturnDate,
    localTravelMode,
    onTravelModeChange,
  ]);

  // ✅ 4. AUTO-REFRESH ON CURRENCY CHANGE
  const prevCurrency = useRef(userCurrency);

  useEffect(() => {
    // Only run if currency actually changed (avoids double-search on mount)
    if (userCurrency && prevCurrency.current !== userCurrency) {
      handleInternalSubmit();
      prevCurrency.current = userCurrency; // Update ref
    }
  }, [userCurrency, handleInternalSubmit]);

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "center",
          mt: 0,
          mb: 3,
          width: { xs: "100%", md: "100%" },
          maxWidth: "1200px",
          mx: "auto",
          border:
            theme.palette.mode === "dark" ? "1px solid #444" : "1px solid #ddd",
          borderRadius: "16px",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0px 3px 10px rgba(0, 0, 0, 0.5)"
              : "0px 3px 10px rgba(0, 0, 0, 0.15)",
          padding: "32px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          <IconButton onClick={() => (window.location.href = "/")}>
            <ArrowBackIcon />
          </IconButton>

          <Typography variant="h5" fontWeight={500}>
            Explore Search
          </Typography>
        </Box>

        {/* Origin Autocomplete */}
        <Autocomplete
          sx={{
            flexGrow: 1,
            minWidth: 260,
            maxWidth: 400,
          }}
          options={originOptions || []}
          getOptionLabel={(option) =>
            typeof option === "string"
              ? option
              : `${option.city}, ${option.country_name || option.country || ""}`
          }
          inputValue={originInput}
          onInputChange={(_, newValue) => setOriginInput(newValue)}
          value={origin}
          onChange={(_, newValue) => {
            if (setOrigin) setOrigin(newValue);
            setOriginInput(
              newValue?.city
                ? `${newValue.city}, ${
                    newValue.country_name || newValue.country
                  }`
                : ""
            );
          }}
          filterOptions={(options, { inputValue }) =>
            options.filter((option) => {
              const cleanInput = normalizeInput(inputValue.trim()).toLowerCase();
              const city = normalizeInput(option.city || "").toLowerCase();
              const country = normalizeInput(
                option.country_name || option.country || ""
              ).toLowerCase();
              const iataMatch = option.airports?.some(
                (a) => normalizeInput(a.iata || "").toLowerCase().startsWith(cleanInput)
              );

              return (
                city.includes(cleanInput) ||
                country.includes(cleanInput) ||
                iataMatch
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Origin"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <FlightTakeoffIcon sx={{ mr: 1 }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        {/* ✅ Date Range Picker using LOCAL STATE */}
        <Box sx={{ flex: "0 0 280px", height: "56px" }}>
          <DateRangeExplore
            departureDate={localDepartureDate} // Use Local
            setDepartureDate={setLocalDepartureDate} // Set Local
            returnDate={localReturnDate} // Use Local
            setReturnDate={setLocalReturnDate} // Set Local
            searchType="explore"
          />
        </Box>

        {/* Travel Time Input */}
        <TextField
          label="Travel (hrs)"
          type="number"
          inputProps={{
            min: 1,
            max: 12,
            style: { textAlign: "center" },
          }}
          value={travelTime}
          onChange={(e) => {
            const newVal = Math.max(
              1,
              Math.min(12, parseInt(e.target.value, 10) || 1)
            );
            setTravelTime(newVal);
          }}
          size="medium"
          sx={{ width: 89 }}
        />

        {/* Travel Mode */}
        <ToggleButtonGroup
          value={localTravelMode}
          exclusive
          onChange={(_, v) => {
            if (v) setLocalTravelMode(v); // 👈 Only update Local State
          }}
          size="medium"
        >
          <ToggleButton
            value="flight"
            sx={{
              height: "56px",
              "&.Mui-selected": {
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            }}
          >
            <FlightTakeoffIcon fontSize="medium" />
          </ToggleButton>
          <ToggleButton
            value="car"
            sx={{
              height: "56px",
              "&.Mui-selected": {
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            }}
          >
            <DirectionsCarIcon fontSize="medium" />
          </ToggleButton>
          <ToggleButton
            value="train"
            sx={{
              height: "56px",
              "&.Mui-selected": {
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            }}
          >
            <TrainIcon fontSize="medium" />
          </ToggleButton>
        </ToggleButtonGroup>

        <Badge
          badgeContent={getSelectedFilterCount()}
          color="primary"
          overlap="rectangular"
          invisible={getSelectedFilterCount() === 0}
          sx={{
            "& .MuiBadge-badge": {
              top: 4,
              right: 4,
              fontSize: "0.7rem",
              fontWeight: "bold",
              height: 20,
              minWidth: 20,
              borderRadius: "50%",
              backgroundColor: theme.palette.mode === "dark" ? "#fff" : "#000",
              color: theme.palette.mode === "dark" ? "#000" : "#fff",
            },
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setFiltersOpen(true)}
            startIcon={<FilterListIcon />}
            sx={{
              height: "56px",
              minWidth: "100px",
              fontWeight: 500,
            }}
          >
            Filters
          </Button>
        </Badge>

        <Button
          variant="contained"
          onClick={handleInternalSubmit} // ✅ Call our fixed handler
          size="medium"
          sx={{
            height: "56px",
            minWidth: "100px",
            fontWeight: 500,
          }}
        >
          Search
        </Button>

        <Box sx={{ mt: 1 }}>
          <Box
            onClick={() => setShowPreferences((prev) => !prev)}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              gap: 0,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={300}
              sx={{
                textAlign: "center",
                color: "primary.main",
                textDecoration: "underline",
                cursor: "pointer",
                transition: "color 0.2s ease, transform 0.2s ease",
                fontSize: "0.9rem",

                "&:hover": {
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.primary.light
                      : theme.palette.primary.dark,
                  transform: "translateY(-1px)",
                },
              }}
            >
              Explore Trip Preferences
            </Typography>
            <IconButton size="medium">
              {showPreferences ? (
                <KeyboardDoubleArrowUpIcon color="primary" />
              ) : (
                <KeyboardDoubleArrowDownIcon color="primary" />
              )}
            </IconButton>
          </Box>

          <Collapse in={showPreferences}>
            <Box sx={{ mt: 1 }}>
              {/* Destination Vibe Selector */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.75, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.7rem" }}
                >
                  Destination Type
                </Typography>
                <ToggleButtonGroup
                  value={destinationVibe}
                  exclusive
                  onChange={(_, val) => { if (val) setDestinationVibe(val); }}
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      py: 0.75,
                      borderColor: "divider",
                    },
                    "& .MuiToggleButton-root.Mui-selected": {
                      fontWeight: 700,
                    },
                  }}
                >
                  <ToggleButton value="under_the_radar">🕵️ Under the Radar</ToggleButton>
                  <ToggleButton value="typical">⚖️ Typical</ToggleButton>
                  <ToggleButton value="touristic">🗺️ Touristic</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Vibe Chips */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 0.5,
                  mb: 2,
                }}
              >
                {[
                  "Any",
                  "🏖️ Beach",
                  "🏙️ City",
                  "🌿 Nature",
                  "🏔️ Remote",
                  "🏛️ Culture",
                  "💑 Romantic",
                  "🧗 Adventure",
                  "👨‍👩‍👧 Family",
                  "✨ Luxury",
                  "💸 Budget",
                ].map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    clickable
                    size="medium"
                    variant={
                      preferences.includes(label) ? "filled" : "outlined"
                    }
                    color={preferences.includes(label) ? "primary" : "default"}
                    onClick={() => {
                      if (label === "Any") {
                        setPreferences(["Any"]);
                      } else {
                        setPreferences((prev) =>
                          prev.includes(label)
                            ? prev.filter((p) => p !== label)
                            : [...prev.filter((p) => p !== "Any"), label]
                        );
                      }
                    }}
                  />
                ))}
              </Box>

              <TextField
                placeholder="Optionally, describe your ideal trip..."
                fullWidth
                multiline
                minRows={1}
                value={aiDescription}
                onChange={(e) => setAiDescription(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AssistantIcon sx={{ fontSize: "1.2rem" }} />
                    </InputAdornment>
                  ),
                  sx: { fontSize: "0.9rem", mb: 0, mt: 0 },
                }}
              />
            </Box>
          </Collapse>
        </Box>
        <AdvancedFiltersModal
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          searchType={searchType}
          advancedFilters={advancedFilters}
          setAdvancedFilters={setAdvancedFilters}
          airbnbType={airbnbType}
          setAirbnbType={setAirbnbType}
          AMENITIES_MAP={AMENITIES_MAP}
        />
      </Box>
    </div>
  );
}
